import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Skeleton from 'react-loading-skeleton';

import { createForm } from '../../../../models/Form';
import { createFormField } from '../../../../models/FormField';
import { modelOf } from '../../../../prop-types';
import CheckoutStore from '../../../../store/CheckoutStore';
import FormFieldType from '../../../../types/FormFieldType';
import RequestState from '../../../../types/RequestState';
import { createOption } from '../../../form/Field';
import ContentForState from '../../../loader/ContentForState';
import CheckoutMethodSelector from '../CheckoutMethodSelector';
import PickupPoint from '../PickupPoint';

/**
 * PickupPointList - Pure presentation component for displaying pickup point options
 * 
 * Architecture:
 * - The OrderCheckout store handles all fetching and auto-selection logic
 * - This component only renders the current state and handles manual user selections
 * - No useEffects for fetching/auto-selecting to prevent unwanted re-renders and API calls
 */
const PickupPointList = ({ checkoutStore, onOrderUpdate }) => {
  const { order, shippingMethodIsLoading, isLoading } = checkoutStore;
  const {
    currentPickupPointState,
    currentPickupPointList,
    pickupPointIsLoading,
    selectedPickupPoint,
  } = order;
  const disabled = pickupPointIsLoading || shippingMethodIsLoading || isLoading;
  const hasPickupPoints = currentPickupPointList?.length > 0;

  const form = useMemo(() => {
    if (!hasPickupPoints) {
      return null;
    }

    const pickupPointListForm = {
      selectedPickupPoint: createFormField(
        {
          type: FormFieldType.BOOLEAN,
          value: selectedPickupPoint?.unique_id,
          required: currentPickupPointList.length > 0,
          name: 'selectedPickupPoint',
        },
        [],
        () => currentPickupPointList.length > 0
      ),
    };

    return createForm(pickupPointListForm);
  }, [hasPickupPoints, selectedPickupPoint?.unique_id, currentPickupPointList.length]);

  const handlePickupPointUpdate = (option) => {
    order.updateSelectedShipping({ pickupPointId: option.value });
    onOrderUpdate({ withoutTimer: true, skipAnalytics: true });
  };

  const createInputOptions = () =>
    currentPickupPointList.map((pickupPoint) =>
      createOption({
        value: pickupPoint.unique_id,
        label: <PickupPoint info={pickupPoint} />,
      })
    );

  const renderSelector = () => {
    if (hasPickupPoints && form) {
      return (
        <CheckoutMethodSelector
          form={form}
          options={createInputOptions()}
          fieldName={'selectedPickupPoint'}
          field={'selectedPickupPoint'}
          disabled={disabled}
          onChange={handlePickupPointUpdate}
        />
      );
    }

    return null;
  };

  const renderContent = () => {
    if (
      currentPickupPointState === RequestState.LOADED &&
      currentPickupPointList.length === 0
    ) {
      return (
        <p className="PickupPointList__no-pickup-points">
          <FormattedMessage
            id="checkout.shippingModule.emptyPickupPointList"
            defaultMessage="No pickup points found. Check for possible spelling errors in your delivery address."
          />
        </p>
      );
    }

    if (!currentPickupPointState) {
      return (
        <p className="PickupPointList__no-pickup-points">
          <FormattedMessage
            id="checkout.shippingModule.emptyAddress"
            defaultMessage="Fill in the address information to select your desired pickup point"
          />
        </p>
      );
    }

    return (
      <ContentForState
        state={currentPickupPointState}
        loadingPlaceholder={
          <Skeleton
            count={5}
            height={100}
            className="PickupPointList__skeleton"
          />
        }
        forLoaded={renderSelector}
      />
    );
  };

  return <div className="PickupPointList">{renderContent()}</div>;
};

PickupPointList.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
};

export default inject('checkoutStore')(observer(PickupPointList));
